import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isSaturday } from 'date-fns'
import { MessageCircle, Phone, Truck } from 'lucide-react'

import { useGlobalPublicConfig } from '@/shared/hooks/use-global-config'
import { useHotelPricingConfig } from '@/shared/hooks/use-hotel-pricing-config'
import { Alert, AlertDescription } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { DriverService, DriverServiceType } from '@monorepo/functions/src/types/services'
import { InfoCircledIcon } from '@radix-ui/react-icons'

// Default hours in case the config is not available
const DEFAULT_AVAILABLE_HOURS = ['11:00', '12:00', '13:00', '14:00', '16:00', '17:00', '18:00']
const DRIVER_SERVICE_HOURS = Array.from({ length: 10 }, (_, i) => `${(i + 9).toString().padStart(2, '0')}:00`)
const DRIVER_CONTACT_PHONE = '645254587'
const DRIVER_CONTACT_WHATSAPP_PHONE = '34645254587'

// Helper function to generate hours within a range
const generateHoursInRange = (startHour: string, endHour: string): string[] => {
    if (!startHour || !startHour.includes(':') || !endHour || !endHour.includes(':')) {
        return []
    }
    const start = parseInt(startHour.split(':')[0], 10)
    const end = parseInt(endHour.split(':')[0], 10)
    if (isNaN(start) || isNaN(end)) {
        return []
    }
    const hours: string[] = []

    for (let hour = start; hour <= end; hour++) {
        hours.push(`${hour.toString().padStart(2, '0')}:00`)
    }

    return hours
}

interface DriverServiceSectionProps {
    driverService: DriverService | undefined
    onDriverServiceChange: (driverService: DriverService | undefined) => void
    checkInTime: string
    checkOutTime?: string
    onCheckInTimeChange: (time: string) => void
    onCheckOutTimeChange?: (time: string) => void
    selectedDates?: { from: Date; to: Date } | null
    className?: string
    showCheckOutTime?: boolean
    showTimeErrors?: boolean
    isDriverTimeManagedExternally?: boolean
}

export function DriverServiceSection({
    driverService,
    onDriverServiceChange,
    checkInTime,
    checkOutTime,
    onCheckInTimeChange,
    onCheckOutTimeChange,
    selectedDates,
    className = '',
    showCheckOutTime = true,
    showTimeErrors = false,
    isDriverTimeManagedExternally = false,
}: DriverServiceSectionProps) {
    const { t } = useTranslation()
    const { config: hotelPricing } = useHotelPricingConfig()
    const { data: globalConfig } = useGlobalPublicConfig()

    // Track individual field errors - these are cleared when the specific field is modified
    const [checkInTimeError, setCheckInTimeError] = useState(false)
    const [checkOutTimeError, setCheckOutTimeError] = useState(false)
    
    const [checkInSelectOpen, setCheckInSelectOpen] = useState(false)
    const [checkOutSelectOpen, setCheckOutSelectOpen] = useState(false)

    // Set individual errors when showTimeErrors becomes true
    useEffect(() => {
        if (showTimeErrors) {
            setCheckInTimeError(! checkInTime)
            setCheckOutTimeError(showCheckOutTime && !checkOutTime)
        } else {
            setCheckInTimeError(false)
            setCheckOutTimeError(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTimeErrors])

    // Close selects when driverService or selectedDates change
    // Use a ref to track previous values and only close if they actually changed
    const prevDriverServiceRef = useRef(driverService)
    const prevSelectedDatesRef = useRef(selectedDates)
    
    useEffect(() => {
        const driverServiceChanged = prevDriverServiceRef.current !== driverService
        const selectedDatesChanged = 
            prevSelectedDatesRef.current?.from?.getTime() !== selectedDates?.from?.getTime() ||
            prevSelectedDatesRef.current?.to?.getTime() !== selectedDates?.to?.getTime()
        
        if (driverServiceChanged || selectedDatesChanged) {
            // Close selects after a small delay to allow React to finish current updates
            const timeoutId = setTimeout(() => {
                setCheckInSelectOpen(false)
                setCheckOutSelectOpen(false)
            }, 0)
            
            prevDriverServiceRef.current = driverService
            prevSelectedDatesRef.current = selectedDates
            
            return () => clearTimeout(timeoutId)
        }
    }, [driverService, selectedDates])

    // Generate available hours from config
    const AVAILABLE_HOURS = (() => {
        if (!globalConfig?.hotelHours) return DEFAULT_AVAILABLE_HOURS

        const morningHours = generateHoursInRange(
            globalConfig.hotelHours.morning.open,
            globalConfig.hotelHours.morning.close,
        )

        const afternoonHours = generateHoursInRange(
            globalConfig.hotelHours.afternoon.open,
            globalConfig.hotelHours.afternoon.close,
        )

        return [...morningHours, ...afternoonHours]
    })()

    // Hours outside of regular operation
    const isOutOfHours = (hour: number) => {
        if (isNaN(hour)) return false

        if (!globalConfig?.hotelHours) return hour < 8 || hour >= 19

        const morningOpen = parseInt(globalConfig.hotelHours.morning.open.split(':')[0], 10)
        const morningClose = parseInt(globalConfig.hotelHours.morning.close.split(':')[0], 10)
        const afternoonOpen = parseInt(globalConfig.hotelHours.afternoon.open.split(':')[0], 10)
        const afternoonClose = parseInt(globalConfig.hotelHours.afternoon.close.split(':')[0], 10)

        return hour < morningOpen || (hour > morningClose && hour < afternoonOpen) || hour > afternoonClose
    }

    const isOutsideDriverServiceHours = (time: string) => {
        if (!time || !time.includes(':')) return false
        const hour = parseInt(time.split(':')[0], 10)
        if (isNaN(hour)) return false
        return hour < 9 || hour > 18
    }

    const checkInHour = checkInTime && checkInTime.includes(':') ? parseInt(checkInTime.split(':')[0], 10) : NaN
    const checkOutHour = checkOutTime && checkOutTime.includes(':') ? parseInt(checkOutTime.split(':')[0], 10) : NaN

    const isSaturdayCheckIn = selectedDates?.from && isSaturday(selectedDates.from)
    const isSaturdayCheckOut = selectedDates?.to && isSaturday(selectedDates.to)
    const shouldHideTimeSelectionForDriver = Boolean(driverService && isDriverTimeManagedExternally)
    const whatsappMessage = encodeURIComponent(
        t(
            'booking.step2.services.driverContact.whatsappMessage',
            'Hola, quiero coordinar la hora del servicio de chofer para mi reserva del hotel.',
        ),
    )

    // Driver service is completely disabled only if both start and end are on Saturday
    const isDriverServiceDisabled = isSaturdayCheckIn && isSaturdayCheckOut

    // Determine which driver services are enabled based on Saturday restrictions
    const isDriverPickupEnabled = !isSaturdayCheckIn
    const isDriverDropoffEnabled = !isSaturdayCheckOut

    const handleDriverServiceEnable = (checked: boolean) => {
        if (checked && !isDriverServiceDisabled) {
            // Reset times to be within driver service hours if they're outside the range
            // Only adjust if there's actually a time selected (not empty/undefined)
            if (!isDriverTimeManagedExternally && checkInTime && isOutsideDriverServiceHours(checkInTime)) {
                onCheckInTimeChange('09:00')
            }
            if (!isDriverTimeManagedExternally && checkOutTime && isOutsideDriverServiceHours(checkOutTime)) {
                onCheckOutTimeChange?.('18:00')
            }
            // If times are empty/undefined, don't set them - let user select manually

            // Determine service type based on Saturday restrictions
            let serviceType: DriverServiceType = 'both'
            if (!isDriverPickupEnabled && isDriverDropoffEnabled) {
                serviceType = 'dropoff' // Only delivery if pickup is disabled
            } else if (isDriverPickupEnabled && !isDriverDropoffEnabled) {
                serviceType = 'pickup' // Only pickup if dropoff is disabled
            }

            const newService: DriverService = {
                type: 'driver',
                serviceType,
                isOutOfHours: false,
            }
            onDriverServiceChange(newService)
        } else {
            onDriverServiceChange(undefined)
        }
    }

    // Automatically adjust or remove driver service based on Saturday restrictions
    useEffect(() => {
        if (driverService) {
            if (isDriverServiceDisabled) {
                // Remove service completely if both start and end are Saturday
                onDriverServiceChange(undefined)
            } else if (!isDriverPickupEnabled && isDriverDropoffEnabled && driverService.serviceType !== 'dropoff') {
                // Adjust to dropoff only if pickup is disabled
                const adjustedService: DriverService = {
                    ...driverService,
                    serviceType: 'dropoff',
                }
                onDriverServiceChange(adjustedService)
            } else if (isDriverPickupEnabled && !isDriverDropoffEnabled && driverService.serviceType !== 'pickup') {
                // Adjust to pickup only if dropoff is disabled
                const adjustedService: DriverService = {
                    ...driverService,
                    serviceType: 'pickup',
                }
                onDriverServiceChange(adjustedService)
            }
        }
    }, [driverService, isDriverServiceDisabled, isDriverPickupEnabled, isDriverDropoffEnabled, onDriverServiceChange])

    const handleDriverServiceTypeChange = (value: DriverServiceType) => {
        queueMicrotask(() => {
            if (driverService) {
                // Check if the selected value is allowed based on Saturday restrictions
                const isValueAllowed =
                    (value === 'pickup' && isDriverPickupEnabled) ||
                    (value === 'dropoff' && isDriverDropoffEnabled) ||
                    (value === 'both' && isDriverPickupEnabled && isDriverDropoffEnabled)

                if (isValueAllowed) {
                    const newService: DriverService = {
                        ...driverService,
                        serviceType: value,
                    }
                    onDriverServiceChange(newService)
                }
            }
        })
    }

    const handleCheckInTimeChange = (time: string) => {
        queueMicrotask(() => {
            // If driver service is active, validate time is provided and within allowed hours
            if (!isDriverTimeManagedExternally && driverService && time && isOutsideDriverServiceHours(time)) {
                return
            }

            // Clear individual error when this field is modified
            setCheckInTimeError(false)
            onCheckInTimeChange(time)
        })
    }

    const handleCheckOutTimeChange = (time: string) => {
        queueMicrotask(() => {
            // If driver service is active, validate time is provided and within allowed hours
            if (!isDriverTimeManagedExternally && driverService && time && isOutsideDriverServiceHours(time)) {
                return
            }

            // Clear individual error when this field is modified
            setCheckOutTimeError(false)
            onCheckOutTimeChange?.(time)
        })
    }

    return (
        <div className={`space-y-4 ${className}`}>
            <div className='flex items-center space-x-2'>
                <Checkbox
                    id='transport'
                    checked={!!driverService}
                    onCheckedChange={handleDriverServiceEnable}
                    disabled={isDriverServiceDisabled}
                />
                <Label
                    htmlFor='transport'
                    className={`flex items-center ${isDriverServiceDisabled ? 'text-muted-foreground' : ''}`}
                >
                    <Truck className='mr-2 h-4 w-4' />
                    {t('booking.step2.services.driver', 'Servicio de chofer')}
                </Label>
            </div>

            {/* Saturday driver service restrictions banner */}
            {isDriverServiceDisabled && (
                <Alert variant='info'>
                    <div className='flex items-start space-x-3'>
                        <InfoCircledIcon className='mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600' />
                        <AlertDescription>
                            {t(
                                'booking.step2.alerts.saturdayDriverServiceDisabled',
                                'El servicio de chofer no está disponible cuando la reserva empieza y termina en sábado. Por favor, selecciona otras fechas si necesitas este servicio.',
                            )}
                        </AlertDescription>
                    </div>
                </Alert>
            )}

            {/* Saturday partial restrictions banner */}
            {(!isDriverPickupEnabled || !isDriverDropoffEnabled) && !isDriverServiceDisabled && (
                <Alert variant='info'>
                    <div className='flex items-start space-x-3'>
                        <InfoCircledIcon className='mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600' />
                        <AlertDescription>
                            {!isDriverPickupEnabled &&
                                isDriverDropoffEnabled &&
                                t(
                                    'booking.step2.alerts.saturdayStartRestriction',
                                    'Como la reserva empieza en sábado, solo está disponible el servicio de entrega.',
                                )}
                            {isDriverPickupEnabled &&
                                !isDriverDropoffEnabled &&
                                t(
                                    'booking.step2.alerts.saturdayEndRestriction',
                                    'Como la reserva termina en sábado, solo está disponible el servicio de recogida.',
                                )}
                        </AlertDescription>
                    </div>
                </Alert>
            )}

            {driverService ? (
                <div className='space-y-4'>
                    <div className='space-y-2'>
                        <Select 
                            value={driverService.serviceType} 
                            onValueChange={handleDriverServiceTypeChange}
                            key={`driver-type-${isDriverPickupEnabled}-${isDriverDropoffEnabled}`}
                        >
                            <SelectTrigger className='w-[180px]'>
                                <SelectValue
                                    placeholder={t('booking.step2.services.driverType', 'Selecciona el tipo')}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {isDriverPickupEnabled && (
                                    <SelectItem value='pickup'>
                                        {t('booking.step2.services.driverType.pickup', 'Solo recogida')}
                                    </SelectItem>
                                )}
                                {isDriverDropoffEnabled && (
                                    <SelectItem value='dropoff'>
                                        {t('booking.step2.services.driverType.dropoff', 'Solo entrega')}
                                    </SelectItem>
                                )}
                                {isDriverPickupEnabled && isDriverDropoffEnabled && (
                                    <SelectItem value='both'>
                                        {t('booking.step2.services.driverType.both', 'Recogida y entrega')}
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    <Alert variant='info'>
                        <div className='flex items-start space-x-3'>
                            <InfoCircledIcon className='mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600' />
                            <div className='space-y-2'>
                                <AlertDescription>
                                    {t(
                                        'booking.step2.services.driverPriceInfo',
                                        'El precio final dependerá de la ubicación y se acordará directamente con el hotel.',
                                    )}
                                </AlertDescription>
                                <div className='flex items-center space-x-2 text-sm text-blue-700'>
                                    <span className='whitespace-pre-line'>{hotelPricing?.driver.priceExamples}</span>
                                </div>
                            </div>
                        </div>
                    </Alert>
                    {shouldHideTimeSelectionForDriver && (
                        <div className='space-y-3 rounded-md border border-blue-200 bg-blue-50 p-4'>
                            <p className='text-sm text-blue-900'>
                                {t(
                                    'booking.step2.services.driverContact.description',
                                    'La hora del chofer se coordina directamente con el hotel.',
                                )}
                            </p>
                            <div className='flex flex-col gap-2 sm:flex-row'>
                                <Button asChild className='w-full sm:w-auto'>
                                    <a
                                        href={`https://wa.me/${DRIVER_CONTACT_WHATSAPP_PHONE}?text=${whatsappMessage}`}
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        <MessageCircle className='mr-2 h-4 w-4' />
                                        {t('booking.step2.services.driverContact.whatsapp', 'Agendar por WhatsApp')}
                                    </a>
                                </Button>
                                <Button asChild variant='outline' className='w-full sm:w-auto'>
                                    <a href={`tel:${DRIVER_CONTACT_PHONE}`}>
                                        <Phone className='mr-2 h-4 w-4' />
                                        {t('booking.step2.services.driverContact.phone', 'Llamar al 645254587')}
                                    </a>
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            ) : <></>}

            {/* Time Selection Section */}
            {!shouldHideTimeSelectionForDriver && <div id='time-selection-section'>
                <div className={`grid grid-cols-1 gap-4 ${showCheckOutTime ? 'sm:grid-cols-2' : ''}`}>
                    <div className='space-y-2'>
                        <Label htmlFor='checkInTime'>
                            {!isDriverTimeManagedExternally &&
                            (driverService?.serviceType === 'pickup' || driverService?.serviceType === 'both')
                                ? t('booking.step2.time.preferredPickup', 'Hora de recogida preferida')
                                : t('booking.step2.time.checkIn', 'Hora de recogida')}
                        </Label>
                        <Select
                            open={checkInSelectOpen}
                            onOpenChange={setCheckInSelectOpen}
                            onValueChange={handleCheckInTimeChange}
                            value={checkInTime && checkInTime.trim() !== '' ? checkInTime : undefined}
                        >
                            <SelectTrigger className='w-full'>
                                <SelectValue placeholder={t('booking.step2.time.selectTime', 'Selecciona una hora')} />
                            </SelectTrigger>
                            <SelectContent>
                                {driverService
                                    ? !isDriverTimeManagedExternally
                                        ? DRIVER_SERVICE_HOURS.map(hour => (
                                              <SelectItem key={hour} value={hour}>
                                                  {hour}
                                              </SelectItem>
                                          ))
                                        : (isSaturdayCheckIn
                                              ? AVAILABLE_HOURS.filter(hour => parseInt(hour) < 14)
                                              : AVAILABLE_HOURS
                                          ).map(hour => (
                                              <SelectItem key={hour} value={hour}>
                                                  {hour}
                                              </SelectItem>
                                          ))
                                    : (isSaturdayCheckIn
                                          ? AVAILABLE_HOURS.filter(hour => parseInt(hour) < 14)
                                          : AVAILABLE_HOURS
                                      ).map(hour => (
                                          <SelectItem key={hour} value={hour}>
                                              {hour}
                                          </SelectItem>
                                      ))}
                            </SelectContent>
                        </Select>
                        {checkInTimeError && !checkInTime && (
                            <p className='text-sm text-destructive'>
                                {t('booking.step2.time.required', 'Este campo es obligatorio')}
                            </p>
                        )}
                    </div>

                    {showCheckOutTime && checkOutTime !== undefined ? (
                        <div className='space-y-2'>
                            <Label htmlFor='checkOutTime'>
                                {!isDriverTimeManagedExternally &&
                                (driverService?.serviceType === 'dropoff' || driverService?.serviceType === 'both')
                                    ? t('booking.step2.time.preferredDropoff', 'Hora de salida preferida')
                                    : t('booking.step2.time.checkOut', 'Hora de salida')}
                            </Label>
                            <Select
                                open={checkOutSelectOpen}
                                onOpenChange={setCheckOutSelectOpen}
                                onValueChange={handleCheckOutTimeChange}
                                value={checkOutTime && checkOutTime.trim() !== '' ? checkOutTime : undefined}
                            >
                                <SelectTrigger className='w-full'>
                                    <SelectValue
                                        placeholder={t('booking.step2.time.selectTime', 'Selecciona una hora')}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {driverService
                                        ? !isDriverTimeManagedExternally
                                            ? DRIVER_SERVICE_HOURS.map(hour => (
                                                  <SelectItem key={hour} value={hour}>
                                                      {hour}
                                                  </SelectItem>
                                              ))
                                            : (isSaturdayCheckOut
                                                  ? AVAILABLE_HOURS.filter(hour => parseInt(hour) < 14)
                                                  : AVAILABLE_HOURS
                                              ).map(hour => (
                                                  <SelectItem key={hour} value={hour}>
                                                      {hour}
                                                  </SelectItem>
                                              ))
                                        : (isSaturdayCheckOut
                                              ? AVAILABLE_HOURS.filter(hour => parseInt(hour) < 14)
                                              : AVAILABLE_HOURS
                                          ).map(hour => (
                                              <SelectItem key={hour} value={hour}>
                                                  {hour}
                                              </SelectItem>
                                          ))}
                                </SelectContent>
                            </Select>
                            {checkOutTimeError && !checkOutTime && (
                                <p className='text-sm text-destructive'>
                                    {t('booking.step2.time.required', 'Este campo es obligatorio')}
                                </p>
                            )}
                        </div>
                    ) : <></>}
                </div>
            </div>}

            {/* Alerts Section */}
            {(isSaturdayCheckIn ||
                (showCheckOutTime && isSaturdayCheckOut) ||
                (!isNaN(checkInHour) && isOutOfHours(checkInHour)) ||
                (showCheckOutTime && checkOutTime && !isNaN(checkOutHour) && isOutOfHours(checkOutHour))) && (
                <div className='space-y-2'>
                    {isSaturdayCheckIn &&
                        (!driverService ||
                            (driverService.serviceType !== 'pickup' && driverService.serviceType !== 'both')) && (
                            <Alert variant='destructive'>
                                <AlertDescription>{t('booking.step2.alerts.saturdayCheckIn')}</AlertDescription>
                            </Alert>
                        )}
                    {showCheckOutTime &&
                        isSaturdayCheckOut &&
                        (!driverService ||
                            (driverService.serviceType !== 'dropoff' && driverService.serviceType !== 'both')) && (
                            <Alert variant='destructive'>
                                <AlertDescription>{t('booking.step2.alerts.saturdayCheckOut')}</AlertDescription>
                            </Alert>
                        )}
                    {((!isNaN(checkInHour) &&
                        isOutOfHours(checkInHour) &&
                        (!driverService ||
                            (driverService.serviceType !== 'pickup' && driverService.serviceType !== 'both'))) ||
                        (showCheckOutTime &&
                            checkOutTime &&
                            !isNaN(checkOutHour) &&
                            isOutOfHours(checkOutHour) &&
                            (!driverService ||
                                (driverService.serviceType !== 'dropoff' &&
                                    driverService.serviceType !== 'both')))) && (
                        <Alert variant='destructive'>
                            <AlertDescription>{t('booking.step2.alerts.outOfHours')}</AlertDescription>
                        </Alert>
                    )}
                </div>
            )}
        </div>
    )
}
